export enum ProposalTemplateTypeEnum {
  opensolar = 'opensolar',
  pvprosell = 'pvprosell',
  bespoke = 'bespoke',
  proposalV2_nonsolar = 'proposalV2_nonsolar',
  proposalV2_grapes_swipe = 'proposalV2_grapes_swipe',
  proposalV2_bespoke = 'proposalV2_bespoke',
}

export type DialogPreviewName = 'AboutUs' | 'CaseStudies' | 'NextSteps'

export type InlineIconPreviewEndAdornment = {
  action: 'preview'
  adornment: 'icon'
  dialog_name: DialogPreviewName
  tooltips: string
  disableWhenEmpty: boolean
}

export type FieldLabelEndAdornment = InlineIconPreviewEndAdornment
const PROPOSAL_V2_TYPES = [
  ProposalTemplateTypeEnum.proposalV2_grapes_swipe,
  ProposalTemplateTypeEnum.proposalV2_nonsolar,
  ProposalTemplateTypeEnum.proposalV2_bespoke,
] as const

export type ProposalV2TemplateType = (typeof PROPOSAL_V2_TYPES)[number]

export const isProposalV2Type = (type: ProposalTemplateTypeEnum | string | undefined): type is ProposalV2TemplateType =>
  !!type && (PROPOSAL_V2_TYPES as readonly string[]).includes(type)

const PROPOSAL_TEMPLATE_TYPE_VALUES = new Set<string>(Object.values(ProposalTemplateTypeEnum))

/** Narrow arbitrary settings `type` strings to known enum members (unknown/legacy → undefined). */
export const toProposalTemplateTypeEnum = (
  type: ProposalTemplateTypeEnum | string | undefined
): ProposalTemplateTypeEnum | undefined => {
  if (type == null || type === '') return undefined
  return PROPOSAL_TEMPLATE_TYPE_VALUES.has(type) ? (type as ProposalTemplateTypeEnum) : undefined
}

// Temporary fix for MyEnergy layout
// TODO: refactor MyEnergy layout to use flex box https://opensolar.atlassian.net/browse/GS-8614
export const isFlexIframeLayout = (type: ProposalTemplateTypeEnum | string | undefined): boolean => {
  if (!type) return false
  if (type === ProposalTemplateTypeEnum.bespoke) return true
  if (isProposalV2Type(type) && type !== ProposalTemplateTypeEnum.proposalV2_grapes_swipe) {
    return true
  }
  return false
}

/** Proposal V2 iframes that post document height to the host; the host scrolls once (not inside the iframe). */
const AUTO_HEIGHT_PROPOSAL_IFRAME_TYPES = [
  ProposalTemplateTypeEnum.proposalV2_nonsolar,
  ProposalTemplateTypeEnum.proposalV2_bespoke,
] as const

export const isAutoHeightProposalIframe = (
  type: ProposalTemplateTypeEnum | string | undefined
): boolean => !!type && (AUTO_HEIGHT_PROPOSAL_IFRAME_TYPES as readonly string[]).includes(type)

/**
 * Resolves the effective proposal template type from merged `proposal_template_settings`.
 *
 * The host normally sets `settings.type` from the linked `ProposalTemplate`, but older rows
 * or partial overrides can leave `type` on a classic value while `proposal_v2_config` already
 * points at the Non-Solar iframe (`pathname: 'non-solar'`). Without this helper the SPA falls
 * back to the legacy solar proposal chrome even on non-solar projects.
 */
export const resolveProposalTemplateType = (
  proposalTemplateSettings:
    | { type?: string; proposal_v2_config?: { type?: string; view_config?: { pathname?: string } } }
    | undefined
): ProposalTemplateTypeEnum | string | undefined => {
  if (!proposalTemplateSettings) return undefined

  const topLevel = proposalTemplateSettings.type
  if (isProposalV2Type(topLevel)) return topLevel

  const v2 = proposalTemplateSettings.proposal_v2_config
  if (v2?.type && isProposalV2Type(v2.type)) return v2.type
  if (v2?.view_config?.pathname === 'non-solar') {
    return ProposalTemplateTypeEnum.proposalV2_nonsolar
  }

  return topLevel
}
